import { useState } from "react";
import { Link } from "react-router-dom";
import { requestPasswordReset } from "../api/api";
import ValphaLogo from "../branding/logo";

const ForgotPassword = () => {
  const [email, setEmail] = useState("");
  const [error, setError] = useState("");
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [isSubmitted, setIsSubmitted] = useState(false);

  const handleSubmit = async (event) => {
    event.preventDefault();
    if (isSubmitting) return;

    setError("");
    setIsSubmitting(true);
    try {
      await requestPasswordReset(email);
      setIsSubmitted(true);
    } catch (err) {
      setError(err?.message || "We couldn't send the reset link. Please try again.");
    } finally {
      setIsSubmitting(false);
    }
  };

  return (
    <div className="relative min-h-screen flex items-center justify-center bg-[#0F1826] font-inter text-white overflow-hidden px-4 sm:px-6">
      <div className="absolute inset-0 z-10 bg-gradient-to-r from-[#0F1826]/90 to-[#0F1826]/90" />
      <div className="relative z-20 bg-[#1A2539]/80 backdrop-blur-md p-5 sm:p-8 rounded-xl shadow-lg w-full max-w-[340px] sm:max-w-[384px]">
        <div className="flex flex-col items-center mb-6 space-y-3 text-center">
          <img
            src={ValphaLogo}
            alt="valpha.io"
            className="h-14 sm:h-16 w-auto object-contain bg-transparent select-none"
          />
          <h1 className="text-2xl font-bold tracking-tight">Reset your password</h1>
          <p className="text-sm text-gray-400">
            {isSubmitted
              ? "Check your inbox for the next step."
              : "Enter the email address linked to your account."}
          </p>
        </div>

        {isSubmitted ? (
          <div className="space-y-5 text-center">
            <div className="mx-auto flex h-12 w-12 items-center justify-center rounded-full bg-[#4FA9A1]/15 text-[#4FA9A1]">
              <svg className="h-7 w-7" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                <path strokeLinecap="round" strokeLinejoin="round" d="M4 4h16v16H4zM4 7l8 6 8-6" />
              </svg>
            </div>
            <p className="text-sm leading-6 text-gray-300">
              If an account exists for <span className="text-white">{email}</span>,
              we&apos;ve sent a password reset link.
            </p>
            <Link
              to="/login"
              className="inline-flex bg-[#4FA9A1] hover:bg-[#3e938b] transition-colors text-[#0F1826] font-semibold py-2 px-6 rounded-lg text-sm"
            >
              Back to Login
            </Link>
          </div>
        ) : (
          <>
            {error && (
              <p role="alert" className="text-red-400 text-center mb-4 text-sm">
                {error}
              </p>
            )}
            <form onSubmit={handleSubmit} className="space-y-4">
              <label className="block">
                <span className="sr-only">Email address</span>
                <input
                  type="email"
                  placeholder="Email address"
                  className="w-full p-3 rounded-lg bg-[#0F1826]/60 border border-[#2A3954] text-white placeholder-gray-400 focus:outline-none focus:ring-2 focus:ring-[#4FA9A1]"
                  value={email}
                  onChange={(event) => setEmail(event.target.value)}
                  required
                  autoComplete="email"
                  autoFocus
                />
              </label>
              <button
                type="submit"
                disabled={isSubmitting}
                className="w-full bg-[#4FA9A1] hover:bg-[#3e938b] disabled:cursor-not-allowed disabled:opacity-60 transition-colors text-[#0F1826] font-semibold py-2 rounded-lg"
              >
                {isSubmitting ? "Sending..." : "Send reset link"}
              </button>
            </form>
            <p className="text-center text-sm text-gray-400 mt-4">
              Remembered your password?{" "}
              <Link to="/login" className="text-[#4FA9A1] hover:underline">
                Back to Login
              </Link>
            </p>
          </>
        )}
      </div>
    </div>
  );
};

export default ForgotPassword;
